import { getNowInTimeZone, useFeatureFlags } from '@chronosphereio/core';
import { DateCalendar, DateRangeCalendar, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { useCallback, useMemo, useState } from 'react';
import { useIsMobile } from '../../../theme';
import { Button } from '../../Button';
import { Stack } from '../../Stack';
import { isValidTimeRange } from '../utils';
import {
  BaseCalendarProps,
  BaseInputCalendarWrapper,
  computeCalendarValue,
  processDateRangeSelection,
} from './BaseInputCalendarWrapper';
import { DateTimeInputFields } from './DateTimeInputFields';
import { useCalendarRangeInteraction } from './useCalendarRangeInteraction';

/** Represents a time range with nullable dates. */
export interface TimeRange {
  end: Date | null;
  start: Date | null;
}

export interface StructuredInputCalendarProps extends BaseCalendarProps {
  /** Callback when the user applies a time range. Returns a TimeRange with nullable dates. */
  onApply: (timeRange: TimeRange) => void;
  /** Whether to combine date and time into a single input field. */
  combineDateTimeInput?: boolean;
  /** Initial time range with nullable dates. */
  initialTimeRange?: TimeRange;
  /** Use structured date/time input fields (MUI DateTimeField/TimePicker). This is the default mode. */
  inputMode?: 'structured';
  /** Callback when the user cancels. */
  onCancel?: () => void;
}

export const StructuredInputCalendar = ({
  initialTimeRange,
  minDate,
  maxDate,
  onCancel,
  onApply,
  variant = 'single',
  timeFormat,
  combineDateTimeInput = false,
  timeZone,
}: StructuredInputCalendarProps) => {
  // Using viewport-based source for layout because this element is usually rendered in a popover
  // and thus is more impacted by the available viewport (not a specific element on the page).
  // This overrides the default breakpointSource (element).
  const isMobile = useIsMobile({ breakpointSource: 'viewport' });
  const [timeRange, setTimeRange] = useState(initialTimeRange ?? { start: null, end: null });

  // Track previous initialTimeRange to detect changes and sync state during render
  const [prevInitialTimeRange, setPrevInitialTimeRange] = useState(initialTimeRange);
  if (initialTimeRange !== prevInitialTimeRange) {
    setPrevInitialTimeRange(initialTimeRange);
    if (initialTimeRange) {
      setTimeRange(initialTimeRange);
    }
  }

  const [isTimeModified, setIsTimeModified] = useState(false);
  const { start, end } = timeRange;
  const isSingleDatePicker = variant === 'single';
  const calendarValue = useMemo(() => computeCalendarValue(start, end), [start, end]);

  const handleMonthSelect = useCallback((range: { end: Date; start: Date }) => {
    setTimeRange({ end: range.end, start: range.start });
    setIsTimeModified(false);
  }, []);

  const {
    isSelectingNewStart,
    modifiedEndTime,
    rangePosition,
    handleMonthChange,
    handleCalendarWrapperClick,
    handleRangePositionChange,
    resetInteractionState,
  } = useCalendarRangeInteraction({
    calendarStart: calendarValue.start,
    timeZone,
    minDate,
    maxDate,
    onMonthSelect: handleMonthSelect,
  });

  const isValidTime = useMemo(() => {
    const timeRangeValid = isValidTimeRange({ start, end, isEndOptional: isSingleDatePicker });
    if (isSingleDatePicker) {
      const isStartAfterMin = !minDate || isValidTimeRange({ start: minDate, end: start });
      const isStartBeforeMax = !maxDate || isValidTimeRange({ start, end: maxDate });
      return timeRangeValid && isStartAfterMin && isStartBeforeMax;
    }
    return timeRangeValid;
  }, [start, end, isSingleDatePicker, minDate, maxDate]);

  const onClickApply = () => {
    if (!isValidTime) return;

    onApply(timeRange);
    setIsTimeModified(false);
    resetInteractionState();
  };

  const setEndTimeToNow = () => {
    setTimeRange((prevTimeRange) => ({ ...prevTimeRange, end: getNowInTimeZone(timeZone) }));
  };

  const handleCancel = () => {
    onCancel?.();
    setIsTimeModified(false);
    resetInteractionState();
  };

  const handleTimeInputChange = (newTimeRange: TimeRange) => {
    setTimeRange(newTimeRange);
    // eslint-disable-next-line react-hooks/immutability
    modifiedEndTime.current = newTimeRange['end'];
    setIsTimeModified(true);
  };

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    const result = processDateRangeSelection(value, {
      isSelectingNewStart: isSelectingNewStart.current,
      isTimeModified,
      existingStartTime: timeRange.start,
      existingEndTime: modifiedEndTime.current ?? null,
    });

    if (isSelectingNewStart.current) {
      // eslint-disable-next-line react-hooks/immutability
      isSelectingNewStart.current = false;
    }

    setTimeRange({ start: result.start, end: result.end });
  };

  const { enableNewDateTimePickerFormat } = useFeatureFlags();

  const localizationProviderProps = {
    dateAdapter: AdapterDateFns,
    // By default, MUI expands the month placeholder to "MMMM" (full month name)
    // when typing into the field. Overriding `fieldMonthPlaceholder` forces it
    // to stay as "MMM" matching our date time format.
    localeText: {
      fieldMonthPlaceholder: (params: { contentType: string }) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
    },
  };

  const ActionButtons = (
    <Stack
      direction="row"
      sx={(theme) => ({
        gap: theme.spacing(1),
        padding: enableNewDateTimePickerFormat ? 0 : theme.spacing(1),
        justifyContent: 'flex-end',
      })}
    >
      <Button variant="primary" onClick={onClickApply} disabled={!isValidTime}>
        {enableNewDateTimePickerFormat ? 'Apply time range' : 'Apply'}
      </Button>
      {!enableNewDateTimePickerFormat && onCancel && (
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
      )}
    </Stack>
  );

  const RangeInputFields = (
    <Stack sx={{ width: '100%' }}>
      <DateTimeInputFields
        label="From"
        value={start}
        onChange={(value) => handleTimeInputChange({ ...timeRange, start: value })}
        sx={{
          marginBottom: (theme) => theme.spacing(2),
        }}
        timeFormat={timeFormat}
        combineInput={combineDateTimeInput}
      />
      <DateTimeInputFields
        label="To"
        value={end}
        onChange={(value) => handleTimeInputChange({ ...timeRange, end: value })}
        minTime={start ?? undefined}
        timeFormat={timeFormat}
        combineInput={combineDateTimeInput}
      />
      <Stack alignItems="flex-end" sx={{ margin: (theme) => theme.spacing(1, 0) }}>
        <Button size="small" variant="tertiary" onClick={setEndTimeToNow}>
          Set to now
        </Button>
      </Stack>
    </Stack>
  );

  const DateRangeCal = (
    <BaseInputCalendarWrapper onClickCapture={handleCalendarWrapperClick}>
      <DateRangeCalendar<Date | null>
        data-testid="date-range-calendar"
        value={[calendarValue.start, calendarValue.end]}
        onChange={handleDateRangeChange}
        rangePosition={rangePosition}
        onMonthChange={handleMonthChange}
        onRangePositionChange={handleRangePositionChange}
        minDate={minDate}
        maxDate={maxDate}
      />
    </BaseInputCalendarWrapper>
  );

  if (enableNewDateTimePickerFormat && !isSingleDatePicker) {
    return (
      <LocalizationProvider {...localizationProviderProps}>
        <Stack
          direction="column"
          sx={(theme) => ({
            background: 'inherit',
            padding: theme.spacing(2),
          })}
        >
          <Stack alignItems="center">{RangeInputFields}</Stack>
          {DateRangeCal}
          {ActionButtons}
        </Stack>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider {...localizationProviderProps}>
      <Stack
        direction="row"
        sx={{
          background: 'inherit',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {isSingleDatePicker ? (
          <DateCalendar
            data-testid="date-calendar"
            value={start}
            onChange={(value) => setTimeRange({ ...timeRange, start: value })}
            minDate={minDate}
            maxDate={maxDate}
            sx={{ alignSelf: 'center' }}
          />
        ) : (
          DateRangeCal
        )}
        <Stack
          sx={(theme) => ({
            justifyContent: 'space-between',
            padding: theme.spacing(2),
            ...(isMobile
              ? { borderTop: `1px solid ${theme.palette.divider}` }
              : { borderLeft: `1px solid ${theme.palette.divider}` }),
          })}
        >
          {isSingleDatePicker ? (
            <DateTimeInputFields
              hideDate
              value={start}
              onChange={(value) => handleTimeInputChange({ ...timeRange, start: value })}
              minTime={minDate}
              maxTime={maxDate}
              timeFormat={timeFormat}
            />
          ) : (
            RangeInputFields
          )}

          {ActionButtons}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};
